/**
 * Tells the agents' views which cards the user applied, so each conversation's
 * next message carries them (`AgentDocumentViews`).
 *
 * WHY apart from the landing audit
 *   A card's outcome is what its write returned: applied, or applied in part when
 *   the write landed only some of it. The audit's read after a write is telemetry,
 *   never a label (`logLandingMismatches`), so the agent is told the outcome the
 *   card shows the user, never what the audit read.
 *
 * WHEN
 *   Each path that applies a proposal-engine card tells once that card's outcome
 *   is settled: a chat card's Apply and Apply All, a rule page card's Apply and
 *   Apply All, an Apply All run, a conflict the user resolved, a review's remedy
 *   and comment, a comment card, a reply card, and each comment an edit card
 *   carries once it is written (`writeCardComments`).
 *
 * WHAT is reported, per card (`AppliedCardReport`)
 *   - every card -> the card it belongs to and the number the user sees on it,
 *                   read from the chat's card numbers (`chatCardNumbers`), the
 *                   numbers the core minted when it emitted each card
 *   - an edit    -> the paragraphs it targets, the paragraphs it added under the
 *                   ids it wrote them with, and whether it applied only in part
 *   - a comment  -> the paragraphs it sits on, its text and the words it quotes
 *   - a reply    -> the comment it answers and its text
 *   - a place of a change-throughout card an Apply All run did not write, while
 *     another place of it was written -> the paragraph it is in, not chosen or
 *     not applied
 *   The paragraphs' text before and after is read by each view; the reports of one
 *   card are gathered into one card there.
 */
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import { isCardCommentKey } from "../components/chat/proposal-card/card-comment-selections";
import { chatCardNumbers } from "../components/chat/proposal-card/card-numbers";
import type { AppliedCardIdentity, AppliedCardReport, AppliedCommentReport, AppliedEditReport, AppliedPlaceNotAppliedReport, AppliedReplyReport } from "./anchor/document-events";
import { officeWordDocument } from "./anchor/anchor-tracker-registry";
import { TableChangePreview } from "./anchor/proposal/table-change-preview";
import { PartialRedlineApply } from "./errors";
import type { LandingTarget, MarkupLandingTarget, TextCardLandingTarget } from "./redline-landing-reconciliation";

/** The separator between a card's id and the part of the card a rule id names after it. */
const CARD_ID_SEPARATOR = "#";

/**
 * An edit a write attempted: the card it belongs to, the paragraphs it targets and
 * the paragraphs it adds under their ids.
 */
export interface AttemptedEdit {
    cardId: string | null;
    paragraphBookmarkNames: string[];
    addedParagraphBookmarkNames: string[];
    tableChangeSentences: string[];
}

/**
 * A comment as it was written: the card it belongs to, the paragraphs it sits on, its
 * text and the words it quotes.
 *
 * NULL `quotedText`: the comment quotes no words (`AppliedCommentReport`).
 */
export interface WrittenComment {
    cardId: string | null;
    paragraphBookmarkNames: string[];
    text: string;
    quotedText: string | null;
}

/**
 * The card a rule belongs to, by its id.
 *
 * CASES
 *   - a change-throughout card's place (`placeRuleIdOf`)   -> the card: the id before "#"
 *   - an edit card's own comment (`cardCommentKeyOf`)        -> the card: the id before "#"
 *   - any other rule                                       -> the rule's own id
 *   - none                                                 -> null: no card to gather with
 *
 * EXAMPLE
 *   "msg-7#match_3f2a"     -> "msg-7"
 *   "msg-7#comment_c1"     -> "msg-7"
 *   "9b1d…" (a review rule) -> "9b1d…"
 */
export function cardIdOfRuleId(ruleId: string | null): string | null {
    /** NULL: a write with no rule, such as a rule page card's, belongs to no card. */
    if (ruleId === null) return null;
    const separatorIndex = ruleId.indexOf(CARD_ID_SEPARATOR);
    /**
     * WHEN the rule id carries no "#" (indexOf -1) CONSEQUENCE it is the card's own id.
     * EXAMPLE "msg-7#match_3f2a": separatorIndex 5 -> slice(0, 5) = "msg-7"
     */
    if (separatorIndex === -1) return ruleId;
    return ruleId.slice(0, separatorIndex);
}

/**
 * True for a rule that names one place of a change-throughout card (`placeRuleIdOf`).
 *
 * CASES
 *   - "msg-7#match_3f2a" -> true: a place of card msg-7
 *   - "msg-7#comment_c1" -> false: card msg-7's own comment (`isCardCommentKey`)
 *   - "msg-7"            -> false: the card's own rule
 */
export function isChangeThroughoutPlaceRuleId(ruleId: string): boolean {
    return cardIdOfRuleId(ruleId) !== ruleId && !isCardCommentKey(ruleId);
}

/** The card's id and the number the chat shows on it (`AppliedCardIdentity`). */
function appliedCardIdentityOf(cardId: string | null): AppliedCardIdentity {
    return { cardId, cardNumber: chatCardNumbers.numberOf(cardId) };
}

/**
 * The ids of the paragraphs a card adds.
 *
 * CASES
 *   - a text card      -> each new paragraph's id (`RedlineTextInsertion.bookmark`), which the
 *                          applier writes the paragraph bookmarked with
 *   - a markup card    -> none (null card): its new paragraphs carry no id the card knows
 *   - a new paragraph proposed before it carried an id -> left out: the page names it by no id
 */
function addedParagraphBookmarkNamesOf(textCard: RedlineTextProposal | null): string[] {
    /** NULL: a markup card, whose new paragraphs carry no id. */
    if (textCard === null) return [];
    return textCard.insertions.flatMap((insertion) => (insertion.bookmark ? [insertion.bookmark] : []));
}

/**
 * The edit a write attempts for a card (`AttemptedEdit`): the card's id, the paragraphs
 * the write targets, and the paragraphs its text card adds.
 *
 * NULL `textCard`: a markup card, which adds no paragraph by id.
 */
export function attemptedEditOf(cardId: string | null, paragraphBookmarkNames: string[], textCard: RedlineTextProposal | null): AttemptedEdit {
    return { cardId, paragraphBookmarkNames, addedParagraphBookmarkNames: addedParagraphBookmarkNamesOf(textCard), tableChangeSentences: tableChangeSentencesOf(textCard) };
}

/**
 * The rows and tables a card adds or removes, in the words the card shows under its
 * table (`TableChangePreview.lines`, the same lines `TableChangeSentences` draws).
 *
 * WHY sentences, not ids
 *   A new row's cells carry no id the page can name them by, so the agent hears of the
 *   rows a card added in the card's own words, beside the cells the page lists.
 *
 * EXAMPLE
 *   a row added after the row holding "$15,000" with cells "Audit", "$1,500"
 *   -> ['Adds a row after the row containing "$15,000", with the cells "Audit" and "$1,500".']
 *
 * EMPTY for a markup card (null) and a card with no table change.
 */
function tableChangeSentencesOf(textCard: RedlineTextProposal | null): string[] {
    /** NULL: a markup card carries no table change in words. */
    if (textCard === null) return [];
    return TableChangePreview.lines({
        edits: textCard.tableChanges,
        anchorTextOf: (bookmark) => textCard.paragraphs.find((paragraph) => paragraph.bookmark === bookmark)?.textWhenProposed ?? null,
    });
}

/** An edit the user applied, from the edit a write attempted (`AttemptedEdit`). */
export function appliedEditReportOf(attemptedEdit: AttemptedEdit, appliedInPart: boolean): AppliedEditReport {
    return {
        kind: "edit",
        ...appliedCardIdentityOf(attemptedEdit.cardId),
        paragraphBookmarkNames: attemptedEdit.paragraphBookmarkNames,
        addedParagraphBookmarkNames: attemptedEdit.addedParagraphBookmarkNames,
        tableChangeSentences: attemptedEdit.tableChangeSentences,
        appliedInPart,
    };
}

/** A comment the user applied, from the comment as it was written (`WrittenComment`). */
export function appliedCommentReportOf(writtenComment: WrittenComment): AppliedCommentReport {
    return {
        kind: "comment",
        ...appliedCardIdentityOf(writtenComment.cardId),
        paragraphBookmarkNames: writtenComment.paragraphBookmarkNames,
        text: writtenComment.text,
        quotedText: writtenComment.quotedText,
        notWrittenReason: null,
    };
}

/**
 * An edit card's own comment its write could not add, with why: the card reads partly
 * applied, so the agent hears of the comment it shows as not added.
 *
 * EXAMPLE
 *   "Matches the cure period." on _pay, Word refused it
 *   -> { comment, msg-7, [_pay], "Matches the cure period.", notWrittenReason "Word refused it" }
 */
export function commentNotWrittenReportOf(commentNotWritten: WrittenComment, notWrittenReason: string): AppliedCommentReport {
    return { ...appliedCommentReportOf(commentNotWritten), notWrittenReason };
}

/** The edit a write attempted for a written card (`AttemptedEdit`), from the target the landing audit reads. */
export function attemptedEditOfLandingTarget(landingTarget: TextCardLandingTarget | MarkupLandingTarget): AttemptedEdit {
    /** CASES: a text card adds its insertions by id; a markup card names none. */
    const textCard = landingTarget.kind === "text_card" ? landingTarget.card : null;
    return attemptedEditOf(cardIdOfRuleId(landingTarget.ruleId), landingTarget.bookmarkNames, textCard);
}

/**
 * A written card as the agent's views are told of it.
 *
 * CASES
 *   - a comment                    -> the paragraphs it sits on, its text and the words it is on
 *   - a text card or a markup card -> an edit of its paragraphs, `appliedInPart` when
 *                                     its write landed only some of it
 *
 * EXAMPLE
 *   text card msg-7 on [_pay_terms_net], whole -> { edit, msg-7, #7, [_pay_terms_net], [], appliedInPart false }
 */
export function appliedCardOfLandingTarget(landingTarget: LandingTarget, appliedInPart: boolean): AppliedCardReport {
    if (landingTarget.kind === "comment") {
        return appliedCommentReportOf({ cardId: cardIdOfRuleId(landingTarget.ruleId), paragraphBookmarkNames: landingTarget.bookmarkNames, text: landingTarget.commentText, quotedText: landingTarget.quotedText });
    }
    return appliedEditReportOf(attemptedEditOfLandingTarget(landingTarget), appliedInPart);
}

/**
 * The edit a write that threw still left in the document, as the agent's views are
 * told of it: only an edit's write lands in part, a comment's never does.
 *
 * CASES
 *   - a deleted paragraph written back (`WrittenBackRedlineApply`) -> every write landed: applied
 *   - a partial apply with some writes landed                    -> applied in part
 *   - a partial apply with none landed, or any other error       -> none: nothing of it stands
 *
 * EXAMPLE
 *   PartialRedlineApply landed 2 of 3 on [_fee_host_name] -> [{ edit, [_fee_host_name], appliedInPart true }]
 */
export function appliedCardsAfterWriteError(attemptedEdit: AttemptedEdit, writeError: unknown): AppliedCardReport[] {
    if (!(writeError instanceof PartialRedlineApply)) return [];
    if (writeError.everyWriteLanded) return [appliedEditReportOf(attemptedEdit, false)];
    /** NONE landed (0 of N): the write may have changed nothing, so no card is told. */
    if (writeError.landed === 0) return [];
    return [appliedEditReportOf(attemptedEdit, true)];
}

/**
 * A place of a change-throughout card the run did not write, as the agent's views are told
 * of it beside the places it did write (`AppliedPlaceNotAppliedReport`).
 *
 * EXAMPLE
 *   "msg-9#match_b1" on [_keyword_height_color], not chosen
 *   -> { place_not_applied, msg-9, #9, [_keyword_height_color], "not_chosen" }
 */
export function placeNotAppliedReportOf(placeRuleId: string, paragraphBookmarkNames: string[], outcome: AppliedPlaceNotAppliedReport["outcome"]): AppliedPlaceNotAppliedReport {
    return { kind: "place_not_applied", ...appliedCardIdentityOf(cardIdOfRuleId(placeRuleId)), paragraphBookmarkNames, outcome };
}

/**
 * A reply card as the agent's views are told of it: the id of the comment it answers,
 * as the agent read it in `<comment id="...">`, and the reply's text.
 */
export function appliedReplyCardOf(cardId: string | null, repliedCommentId: string, replyText: string): AppliedReplyReport {
    return { kind: "reply", ...appliedCardIdentityOf(cardId), commentId: repliedCommentId, text: replyText };
}

/**
 * Tells the document's event stream the user applied these cards: the agents'
 * views keep them for each conversation's next message and read the page now.
 *
 * WHEN no card was applied
 *   CONSEQUENCE
 *     nothing is published: no read is owed and no message carries anything.
 */
export function tellAgentsCardsApplied(appliedCards: AppliedCardReport[]): void {
    if (appliedCards.length === 0) return;
    officeWordDocument.events.publishRedlinesApplied(appliedCards);
}
